import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchKeyVersion, rotateKeyVersion, rewrapKeyVersion } from '../utils/adminEncryptionApi';
import Notification from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import Button from '../components/elements/Button';
import Input from '../components/elements/Input';
import Loading from '../components/elements/Loading';
import Layout from '../components/Layout';
import { RefreshCcw, KeyRound, RefreshCw, ShieldCheck } from 'lucide-react';

const VAULT = 'vault';
// Pre-rotation records/files with no version tag of their own - a rewrap
// source only, never a rotate/rewrap target (see server/adminEncryption.js).
const LEGACY = 'legacy';
const versionLabel = (version) => (
  version === VAULT ? 'Vault' : version === LEGACY ? 'Legacy (pre-rotation)' : `Version ${version}`
);

const AdminEncryptionPage = () => {
  const [activeKeyVersion, setActiveKeyVersion] = useState(null);
  const [configuredKeyVersions, setConfiguredKeyVersions] = useState([]);
  const [recordCountsByVersion, setRecordCountsByVersion] = useState({});
  const [fileCountsByVersion, setFileCountsByVersion] = useState({});
  const [vaultConfigured, setVaultConfigured] = useState(false);
  const [vaultTransitKeyName, setVaultTransitKeyName] = useState(null);
  const [vaultKeyInfo, setVaultKeyInfo] = useState(null);
  const [vaultKeyInfoError, setVaultKeyInfoError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [rewrapFrom, setRewrapFrom] = useState('');
  const [rewrapTo, setRewrapTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [vaultRotating, setVaultRotating] = useState(false);
  const [rewrapping, setRewrapping] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [vaultRotateConfirmOpen, setVaultRotateConfirmOpen] = useState(false);
  const [rewrapConfirmOpen, setRewrapConfirmOpen] = useState(false);
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const [notification, setNotification] = useState({ message: '', type: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Encryption Keys | Eternal Locker™';
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    getKeyVersion();
  }, [isLoggedIn, navigate]);

  const getKeyVersion = async () => {
    setLoading(true);
    const data = await fetchKeyVersion();
    if (data.success) {
      setActiveKeyVersion(data.activeKeyVersion);
      setConfiguredKeyVersions(data.configuredKeyVersions);
      setRecordCountsByVersion(data.recordCountsByVersion || {});
      setFileCountsByVersion(data.fileCountsByVersion || {});
      setVaultConfigured(!!data.vaultConfigured);
      setVaultTransitKeyName(data.vaultTransitKeyName || null);
      setVaultKeyInfo(data.vaultKeyInfo || null);
      setVaultKeyInfoError(data.vaultKeyInfoError || null);
      setSelectedVersion(String(data.activeKeyVersion));
      // Default the rewrap pair to the most likely intent: move everything
      // *onto* whichever scheme is currently active (Vault when configured),
      // *from* the first other version offered. `to` has to be resolved first
      // so `from` can be picked as "anything but that" — picking `from` as
      // "the first local version that isn't the active one" finds nothing when
      // only SECRET_KEY (v1) is configured, which left From empty and the
      // Rewrap button silently disabled on exactly the local -> Vault
      // migration this page exists for.
      const options = (data.vaultConfigured ? [...data.configuredKeyVersions, VAULT] : data.configuredKeyVersions).map(String);
      const nextTo = rewrapTo || (data.vaultConfigured ? VAULT : String(data.activeKeyVersion));
      if (!rewrapTo) setRewrapTo(nextTo);
      if (!rewrapFrom) {
        const nextFrom = options.find((v) => v !== nextTo);
        if (nextFrom !== undefined) setRewrapFrom(nextFrom);
      }
    } else {
      setNotification({ message: data.message || 'Failed to fetch key version.', type: 'error' });
    }
    setLoading(false);
  };

  const handleRotate = async () => {
    setConfirmOpen(false);
    const targetVersion = Number(selectedVersion);
    setRotating(true);
    const result = await rotateKeyVersion(targetVersion);
    if (result.success) {
      setNotification({ message: `Active local key version rotated to ${result.activeKeyVersion}.`, type: 'success' });
      await getKeyVersion();
    } else {
      setNotification({ message: result.message || 'Failed to rotate key version.', type: 'error' });
    }
    setRotating(false);
  };

  const handleVaultRotate = async () => {
    setVaultRotateConfirmOpen(false);
    setVaultRotating(true);
    const result = await rotateKeyVersion(VAULT);
    if (result.success) {
      setNotification({ message: `Vault key rotated to version ${result.vaultKeyInfo?.latestVersion}.`, type: 'success' });
      await getKeyVersion();
    } else {
      setNotification({ message: result.message || 'Failed to rotate the Vault key.', type: 'error' });
    }
    setVaultRotating(false);
  };

  const handleRewrap = async () => {
    setRewrapConfirmOpen(false);
    setRewrapping(true);
    const from = rewrapFrom === VAULT ? VAULT : rewrapFrom === LEGACY ? LEGACY : Number(rewrapFrom);
    const to = rewrapTo === VAULT ? VAULT : Number(rewrapTo);
    const result = await rewrapKeyVersion(from, to);
    if (result.success) {
      const { records, files } = result;
      const skipped = (records.skipped || 0) + (files.skipped || 0);
      const failed = (records.failed || 0) + (files.failed || 0);
      setNotification({
        message: `Rewrapped ${records.rewrapped} record(s) and ${files.rewrapped} file(s) from ${result.fromVersion} to ${result.toVersion}.` +
          (skipped ? ` ${skipped} were being edited at the same time and were skipped - run rewrap again to catch them.` : '') +
          (failed ? ` ${failed} failed - see server logs.` : ''),
        type: failed ? 'error' : 'success',
      });
      await getKeyVersion();
    } else {
      setNotification({ message: result.message || 'Failed to rewrap key version.', type: 'error' });
    }
    setRewrapping(false);
  };

  if (loading) {
    return (
      <Layout full={true}>
        <Loading fullScreen={false} />
      </Layout>
    );
  }

  const otherConfiguredVersions = configuredKeyVersions.filter((v) => v !== activeKeyVersion);
  const isRetirable = (version) => (recordCountsByVersion[version] || 0) === 0 && (fileCountsByVersion[version] || 0) === 0;
  const isVaultRetirable = (recordCountsByVersion[VAULT] || 0) === 0 && (fileCountsByVersion[VAULT] || 0) === 0;
  // Only shown/offered when there's actually something to migrate - unlike
  // every other row here, 'legacy' isn't a configured feature, just a
  // leftover of the local scheme's history that may or may not be present.
  const hasLegacy = (recordCountsByVersion[LEGACY] || 0) > 0 || (fileCountsByVersion[LEGACY] || 0) > 0;
  // Every configured local version, plus a 'vault' pseudo-version when Vault
  // is configured - valid rewrap *targets*, and what the counts table lists.
  const rewrapToOptions = vaultConfigured ? [...configuredKeyVersions, VAULT] : configuredKeyVersions;
  // 'legacy' is a valid rewrap *source* only (see server/adminEncryption.js) -
  // added on top of the target list rather than replacing it.
  const rewrapFromOptions = hasLegacy ? [...rewrapToOptions, LEGACY] : rewrapToOptions;

  return (
    <Layout full={true}>
      <Notification
        message={notification.message}
        onClose={() => setNotification({ message: '', type: 'success' })}
        type={notification.type}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Encryption Keys</h1>
        <Button onClick={getKeyVersion} variant="secondary">
          <RefreshCcw size={18} /> Refresh
        </Button>
      </div>

      <div className="bg-primary-light border border-accent-main rounded-lg p-4 mb-6">
        <p className="text-sm opacity-75">Active write path for new vault records and files</p>
        <p className="text-xl font-bold flex items-center gap-2">
          {vaultConfigured ? (
            <>
              <ShieldCheck size={20} className="text-accent-main" /> Vault ({vaultTransitKeyName}, version {vaultKeyInfo?.latestVersion ?? '?'})
            </>
          ) : (
            <>Local (version {activeKeyVersion})</>
          )}
        </p>
        {vaultConfigured && (
          <p className="text-xs opacity-60 mt-1">
            While Vault is configured, every new write uses it automatically — the local "Rotate Active Key Version"
            control below only affects local writes, which won't happen again until Vault is unconfigured.
          </p>
        )}
        {vaultConfigured && vaultKeyInfoError && (
          <p className="text-xs text-red-500 mt-2">
            Couldn't read Vault's key version ({vaultKeyInfoError}) — new writes are still going through Vault
            (that check doesn't touch this), but the AppRole's policy is likely missing read on
            {' '}<code>transit/keys/{vaultTransitKeyName}</code>.
          </p>
        )}
      </div>

      <div className="bg-primary-light border border-accent-main rounded-lg overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-accent-main border-b border-accent-main">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Key Version</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Vault Records</th>
              <th className="px-4 py-3 text-left font-semibold">Files</th>
              <th className="px-4 py-3 text-left font-semibold">Retirable?</th>
            </tr>
          </thead>
          <tbody>
            {configuredKeyVersions.map((version) => (
              <tr key={version} className="border-b border-accent-main opacity-75 hover:opacity-100">
                <td className="px-4 py-3 font-mono">{version}</td>
                <td className="px-4 py-3">
                  {!vaultConfigured && version === activeKeyVersion ? (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-accent-main/20 text-accent-main border border-accent-main/30">
                      Active
                    </span>
                  ) : (
                    <span className="opacity-60">Retired-in-use</span>
                  )}
                </td>
                <td className="px-4 py-3">{recordCountsByVersion[version] ?? 0}</td>
                <td className="px-4 py-3">{fileCountsByVersion[version] ?? 0}</td>
                <td className="px-4 py-3">
                  {!vaultConfigured && version === activeKeyVersion ? (
                    <span className="opacity-50">N/A</span>
                  ) : isRetirable(version) ? (
                    <span className="text-accent-main font-semibold">Yes - remove SECRET_KEY_V{version} on next deploy</span>
                  ) : (
                    <span className="opacity-60">No - rewrap first</span>
                  )}
                </td>
              </tr>
            ))}
            {vaultConfigured && (
              <tr className="border-b border-accent-main opacity-75 hover:opacity-100">
                <td className="px-4 py-3 font-mono flex items-center gap-1">
                  <ShieldCheck size={14} /> vault
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-accent-main/20 text-accent-main border border-accent-main/30">
                    Active
                  </span>
                </td>
                <td className="px-4 py-3">{recordCountsByVersion[VAULT] ?? 0}</td>
                <td className="px-4 py-3">{fileCountsByVersion[VAULT] ?? 0}</td>
                <td className="px-4 py-3">
                  <span className="opacity-50">N/A</span>
                </td>
              </tr>
            )}
            {hasLegacy && (
              <tr className="border-b border-accent-main opacity-75 hover:opacity-100">
                <td className="px-4 py-3 font-mono">legacy</td>
                <td className="px-4 py-3">
                  <span className="opacity-60">Retired-in-use</span>
                </td>
                <td className="px-4 py-3">{recordCountsByVersion[LEGACY] ?? 0}</td>
                <td className="px-4 py-3">{fileCountsByVersion[LEGACY] ?? 0}</td>
                <td className="px-4 py-3">
                  <span className="opacity-60">No - rewrap first</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {vaultConfigured && (
        <div className="bg-primary-light border border-accent-main rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <ShieldCheck size={18} /> Rotate Vault Key
          </h2>
          <p className="text-sm opacity-75 mb-4">
            Bumps the Vault Transit key (<code>{vaultTransitKeyName}</code>) to its next version — a native Vault
            operation, no server restart needed, and takes effect immediately on every instance since Vault (not this
            app) tracks the version. Current version: <strong>{vaultKeyInfo?.latestVersion ?? '?'}</strong>, minimum
            decryptable version: <strong>{vaultKeyInfo?.minDecryptionVersion ?? '?'}</strong>.
          </p>
          <Button onClick={() => setVaultRotateConfirmOpen(true)} disabled={vaultRotating}>
            {vaultRotating ? 'Rotating...' : 'Rotate Vault Key'}
          </Button>
        </div>
      )}

      <div className="bg-primary-light border border-accent-main rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <KeyRound size={18} /> Rotate Active Key Version (local)
        </h2>
        <p className="text-sm opacity-75 mb-4">
          New vault records and file uploads will be wrapped under the version you select here
          {vaultConfigured ? ' — but only once Vault is no longer configured, since Vault takes priority for all new writes until then' : ''}.
          Existing content keeps decrypting under whichever version it was originally wrapped with
          — nothing is re-encrypted. Only versions with a key already configured on the server
          (<code>SECRET_KEY</code> / <code>SECRET_KEY_V{'{n}'}</code>) can be selected; to add a new
          version, provision it in the environment and restart the server first. This only takes
          effect on the server instance handling this request — in a multi-instance deployment,
          repeat this on every instance.
        </p>

        {otherConfiguredVersions.length === 0 ? (
          <p className="text-sm opacity-60">
            No additional key versions are configured. Add <code>SECRET_KEY_V{activeKeyVersion + 1}</code> to
            the environment and restart the server to enable rotation.
          </p>
        ) : (
          <div className="flex items-center gap-3">
            <Input
              type="select"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="max-w-xs"
            >
              {configuredKeyVersions.map((version) => (
                <option key={version} value={version}>
                  Version {version}{version === activeKeyVersion ? ' (active)' : ''}
                </option>
              ))}
            </Input>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={rotating || Number(selectedVersion) === activeKeyVersion}
            >
              {rotating ? 'Rotating...' : 'Rotate'}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-primary-light border border-accent-main rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <RefreshCw size={18} /> Rewrap (retire an old key version)
        </h2>
        <p className="text-sm opacity-75 mb-4">
          Moves every vault record and file still wrapped under one key version onto another —
          local versions or Vault — without re-encrypting any content; only the small wrapped data
          key per record/file changes. Run this until a version's counts above both read zero, then
          that version's <code>SECRET_KEY_V{'{n}'}</code> can be removed from the environment on the
          next deploy.
        </p>
        {vaultConfigured && (
          <p className="text-sm opacity-75 mb-4">
            Selecting <strong>Vault → Vault</strong> re-wraps every Vault-held record onto the
            Transit key's current version — run it after rotating the Vault key so the previous
            version is no longer referenced and its <code>min_decryption_version</code> can be
            raised in Vault. The counts above won't change, since Vault tracks its own key versions
            internally; check <code>{vaultTransitKeyName}</code>'s version state in Vault to confirm.
          </p>
        )}

        {rewrapToOptions.length < 2 && !hasLegacy ? (
          <p className="text-sm opacity-60">
            Only one key version is configured — nothing to rewrap. Add another <code>SECRET_KEY_V{'{n}'}</code>{vaultConfigured ? ' or configure Vault' : ''} to enable this.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm opacity-75">From</label>
            <Input type="select" value={rewrapFrom} onChange={(e) => setRewrapFrom(e.target.value)} className="max-w-xs">
              {rewrapFromOptions.map((version) => (
                <option key={version} value={version}>
                  {versionLabel(version)}
                </option>
              ))}
            </Input>
            <label className="text-sm opacity-75">To</label>
            <Input type="select" value={rewrapTo} onChange={(e) => setRewrapTo(e.target.value)} className="max-w-xs">
              {rewrapToOptions.map((version) => (
                <option key={version} value={version}>
                  {versionLabel(version)}{version === activeKeyVersion && !vaultConfigured ? ' (active)' : ''}
                </option>
              ))}
            </Input>
            <Button
              onClick={() => setRewrapConfirmOpen(true)}
              disabled={rewrapping || !rewrapFrom || !rewrapTo || (rewrapFrom === rewrapTo && rewrapFrom !== VAULT)}
            >
              {rewrapping ? 'Rewrapping...' : 'Rewrap'}
            </Button>
          </div>
        )}

        {vaultConfigured && !isVaultRetirable && (
          <p className="text-xs opacity-60 mt-2">
            Vault still has {recordCountsByVersion[VAULT] ?? 0} record(s) and {fileCountsByVersion[VAULT] ?? 0} file(s) —
            not itself retirable while it's the active write path, shown for completeness only.
          </p>
        )}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Rotate active local key version?"
        message={`New local writes will wrap under key version ${selectedVersion}${vaultConfigured ? ' (only once Vault is no longer configured)' : ''}. This cannot be undone from here without rotating again.`}
        confirmText="Rotate"
        onConfirm={handleRotate}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        open={vaultRotateConfirmOpen}
        title="Rotate the Vault key?"
        message={`This bumps ${vaultTransitKeyName} to a new version in Vault immediately. Existing content keeps decrypting under its original version - nothing is re-encrypted until you rewrap.`}
        confirmText="Rotate"
        onConfirm={handleVaultRotate}
        onCancel={() => setVaultRotateConfirmOpen(false)}
      />

      <ConfirmModal
        open={rewrapConfirmOpen}
        title="Rewrap records and files?"
        message={rewrapFrom === VAULT && rewrapTo === VAULT
          ? `Every Vault-held record and file will be re-wrapped onto ${vaultTransitKeyName}'s current version (${vaultKeyInfo?.latestVersion ?? '?'}). Content itself is never re-encrypted, and the data key plaintext never leaves Vault for this move, but this touches every matching row/file now.`
          : `Every vault record and file currently wrapped under ${rewrapFrom === VAULT ? 'Vault' : rewrapFrom === LEGACY ? 'the pre-rotation format' : `version ${rewrapFrom}`} will be re-wrapped under ${rewrapTo === VAULT ? 'Vault' : `version ${rewrapTo}`}. Content itself is never re-encrypted, but this touches every matching row/file now.`}
        confirmText="Rewrap"
        onConfirm={handleRewrap}
        onCancel={() => setRewrapConfirmOpen(false)}
      />
    </Layout>
  );
};

export default AdminEncryptionPage;
